import { useState, useEffect, useRef, useCallback } from 'react';
import type { FunctionComponent } from 'react';

import Flex from '../../Flex';
import MonthInput from '../Input/MonthInput';
import Icon, { registerIcon } from '../../Icon';
import * as caretLeftIcon from '../../Icon/icons/caret-left.icon';
import * as caretRightIcon from '../../Icon/icons/caret-right.icon';
import { useConfiguration, useI18n, useTestIds } from '../../../hooks';
import { getCalendarTypeFromLocale } from '../Input/utils';
import { getAdjustedUTCDateTime, getAdjustedUTCDateTimeForTimezone } from '../utils';
import { getDatePickerTestIds } from '../DateTime.test-ids';
import type { CalendarType } from '../Input/local';

import Calendar from './Calendar';
import type DatePickerProps from './DatePicker.types';
import {
  StyledDatePicker,
  StyledDatePickerMonthInput,
  StyledMonthNavigationButton,
  StyledMonthPicker
} from './DatePicker.styles';
import { isValidDate, toDateTimeString } from './utils';

registerIcon(caretLeftIcon, caretRightIcon);

export interface DateRangePickerProps extends DatePickerProps {
  /** Defines dates range that can be set by the user. */
  selectedRange?: { startDate: Date | undefined; endDate: Date | undefined };
}

const DateRangePicker: FunctionComponent<DateRangePickerProps> = (props: DateRangePickerProps) => {
  const { locale } = useConfiguration();
  const {
    testId,
    selected: initial,
    selectedRange,
    onChange,
    min,
    max,
    mode,
    showWeekNumber,
    timeZone,
    ...restProps
  } = props;
  const testIds = useTestIds(testId, getDatePickerTestIds);
  const getNow = useCallback(
    () => (timeZone ? getAdjustedUTCDateTimeForTimezone(timeZone) : getAdjustedUTCDateTime()),
    [timeZone]
  );
  const [selected, setSelected] = useState(initial);
  const [current, setCurrent] = useState(() =>
    selected && isValidDate(selected) ? selected : getNow()
  );

  useEffect(() => {
    if (toDateTimeString(initial) !== toDateTimeString(selected)) {
      setSelected(initial);
      setCurrent(initial || getNow());
    }
  }, [toDateTimeString(initial), getNow]);

  const calendarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // need to postpone focusing to make sure onFocusWithin has been attached
    setTimeout(() => {
      calendarRef.current?.querySelector<HTMLElement>('button[tabindex="0"]:enabled')?.focus();
    });
  }, []);

  const calendarType: CalendarType = getCalendarTypeFromLocale(locale);
  const t = useI18n();

  return (
    <StyledDatePicker aria-label={t('calendar')} role='region' {...restProps}>
      <Flex as={StyledMonthPicker} container={{ justify: 'between', alignItems: 'end' }}>
        <MonthInput
          data-testid={testIds.month}
          aria-label={t('picker_jump_to_month_and_year')}
          value={current}
          as={StyledDatePickerMonthInput}
          innerLabelsHidden={false}
          required
          role='group'
          onChange={({ valueAsTimestamp }) => {
            const newCurrent = new Date(valueAsTimestamp!);
            newCurrent.setUTCDate(current.getUTCDate());
            setCurrent(newCurrent);
          }}
          min={min}
          max={max}
        />
        <StyledMonthNavigationButton
          data-testid={testIds.previousMonth}
          label={t('picker_prev_month')}
          variant='simple'
          icon
          onClick={() => {
            const newCurrent = new Date(current);
            newCurrent.setUTCMonth(newCurrent.getUTCMonth() - 1);
            setCurrent(newCurrent);
          }}
        >
          <Icon name='caret-left' />
        </StyledMonthNavigationButton>
        <StyledMonthNavigationButton
          data-testid={testIds.setCurrentDate}
          label={t('set_current_date_a11y')}
          icon
          variant='simple'
          onClick={() => {
            const currDate = getNow();
            setCurrent(currDate);
            setSelected(currDate);
            onChange?.(currDate);
          }}
        >
          <Icon name='calendar-now' />
        </StyledMonthNavigationButton>
        <StyledMonthNavigationButton
          data-testid={testIds.nextMonth}
          label={t('picker_next_month')}
          variant='simple'
          icon
          onClick={() => {
            const newCurrent = new Date(current);
            newCurrent.setUTCMonth(newCurrent.getUTCMonth() + 1);
            setCurrent(newCurrent);
          }}
        >
          <Icon name='caret-right' />
        </StyledMonthNavigationButton>
      </Flex>
      <Flex container={{ wrap: 'nowrap', justify: 'center' }}>
        <Calendar
          ref={calendarRef}
          selected={selected && isValidDate(selected) ? selected : undefined}
          current={current}
          selectedRange={selectedRange}
          showHighlight
          calendarType={calendarType}
          showWeekNumber={showWeekNumber}
          onFocus={date => {
            setCurrent(date);
          }}
          onSelect={date => {
            setCurrent(date);
            setSelected(date);
            onChange?.(date);
          }}
          min={min}
          max={max}
          timeZone={timeZone}
        />
      </Flex>
    </StyledDatePicker>
  );
};

export default DateRangePicker;
